import { I18n } from '@byted-cg/linkchat-plugin';
import { inject, observer } from 'mobx-react';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { AppContextType } from '@byted-cg/linkchat-plugin';
import { Dialog, Toast, Button } from '@byted-cg/linkchat-ui';
import { ColumnNotice, ColumnMute } from '@byte-design/icons';
import { ToastHandler } from '@byted-cg/linkchat-ui/es/components/toast';

import playpng from '../../../../public/play.png';
import pausepng from '../../../../public/pause.png';
import classes from '../constants/classes';
import { prefixCls } from '../utils/style';

import Recorder from 'recorder-core';
import 'recorder-core/src/engine/wav';
import 'recorder-core/src/extensions/waveview';
import 'recorder-core/src/extensions/frequency.histogram.view';
import 'recorder-core/src/extensions/lib.fft.js';

interface AudioWrapperProps {
  appContext: AppContextType;
}

let starRecTime = 0;
let notAllowDialog: any = null;

// 倒计时弹窗
const CountDownText: FC = () => {
  const [count, setCount] = useState(10);
  useEffect(() => {
    const interval = window.setInterval(() => {
      setCount((x) => {
        if (x > 1) {
          return x - 1;
        } else {
          return x;
        }
      });
    }, 1000);
    return () => {
      window.clearInterval(interval);
    };
  }, []);
  return (
    <span>
      {I18n.t('{count}_seconds_left', { count: count }, '还剩 {count} 秒')}
    </span>
  );
};
const ua = window.navigator.userAgent;
const iosSupport = () => {
  try {
    // 必须是iOS
    let info: any = ua.toLowerCase().match(/cpu iphone os (.*?) like mac os/);
    if (!info) return true;
    // safari是支持低版本的，wkwebkit不支持，并且ios16.0.2 版本也有问题
    let version = info[1].replace(/_/g, '.');
    return /Safari/.test(ua) || (version >= '14.3' && version !== '16.0.2');
  } catch (error) {
    return true;
  }
};
// 幸福客支持
const xfkSupport = () => {
  try {
    const isXFK = /ManyHouseF101/.test(ua);
    if (!isXFK) return true;
    const vInfo: any = ua.match(/ManyHouseF101\/(.*?)\s/);
    return vInfo[1] >= '3.8.0';
  } catch (error) {
    return true;
  }
};

const AudioWrapper: React.FC<AudioWrapperProps> = ({ appContext }) => {
  // 录音状态
  const onRecordRef = useRef(false);
  const [onRecord, _setRecordStatus] = useState(onRecordRef.current);
  const setRecordStatus = useCallback((status: boolean) => {
    _setRecordStatus(status);
    onRecordRef.current = status;
  }, []);
  // 音频实例
  const rec = useRef<any>(null);
  // 录音倒计时
  const [intervalTime, setIntervalTime] = useState<any>(null);
  const [downTime, setDownTime] = useState(false);
  // 试听音频相关
  const audioFile = useRef<any>(null);
  const audioDemo = useRef<any>(null);
  // 权限 + 极端case兼容
  const [isRecordEnd, setReocrdEnd] = useState(false);
  const isPermissionSucc = useRef(false);
  const isTouched = useRef(false);
  // 音频控件相关
  const [isPlay, setIsPlay] = useState(false);
  const [playTime, _setPlayTime] = useState('0:00');
  const realPlayTime = useRef(0);
  const setPlayTime = useCallback(() => {
    if (realPlayTime.current >= realTotalTime.current) return;
    realPlayTime.current += 1;
    setPlayedWidth({
      width: `${(realPlayTime.current / realTotalTime.current) * 100}%`,
    });
    dealAudioTime(realPlayTime.current, false);
  }, [realPlayTime]);
  const realTotalTime = useRef(0);
  const [totalTime, setTotalTime] = useState('0:00');
  const [isVoiceOpen, setIsVoiceOpen] = useState(true);
  const processInterval = useRef<any>(null);
  const [playedWidth, setPlayedWidth] = useState({ width: '0px' });
  const dealAudioTime = (duration: number, isTotal?: boolean) => {
    isTotal && (realTotalTime.current = Math.floor(duration));
    let min = Math.floor(duration / 60);
    let sec = Math.floor(duration % 60);
    const final = `${min}:${sec >= 10 ? sec : '0' + sec}`;
    isTotal ? setTotalTime(final) : _setPlayTime(final);
  };
  const clearAudioDemo = () => {
    callPlay(true);
    dealAudioTime(0, false);
    setPlayedWidth({ width: '0px' });
    realPlayTime.current = 0;
  };
  // 音频可视化实例对象
  let wave: any = null;
  // 长按开始录音
  const checkAndStartRec = async (e) => {
    // ios 14.3之后才支持
    if (!iosSupport()) {
      Toast.show(
        I18n.t(
          'the_current_ios_system_does_not_support_voice_sending_function__please_upgrade_t',
          {},
          '当前iOS系统暂不支持语音发送功能，请升级系统版本',
        ),
      );
      return;
    }
    // 针对幸福客需要3.8.0版本之后
    if (!xfkSupport()) {
      Toast.show(
        I18n.t(
          'the_current_app_version_does_not_support_voice_sending_function__please_upgrade_',
          {},
          '当前App版本不支持语音发送功能，请升级至最新版本',
        ),
      );
      return;
    }
    e.preventDefault();
    if (rec.current) return; // 防止创建多个录音实例
    starRecTime = new Date().getTime();
    isTouched.current = true;
    let recTmp: any;
    recTmp = Recorder({
      type: 'wav',
      sampleRate: 16000,
      bitRate: 16,
      onProcess: (
        buffers: string | any[],
        powerLevel: any,
        bufferDuration: any,
        bufferSampleRate: any,
      ) => {
        wave &&
          wave.input(buffers[buffers.length - 1], powerLevel, bufferSampleRate);
      },
    });
    rec.current = recTmp;
    recTmp.open(
      () => {
        console.log('获取权限成功');
        isPermissionSucc.current = true;
        // 防止首次授权的时候会自动触发录音
        if (!isTouched.current && rec.current) {
          rec.current.close();
          rec.current = null;
          setRecordStatus(false);
          return;
        }
        setRecordStatus(true);
        setTimeout(() => {
          wave = Recorder.FrequencyHistogramView({
            elem: '.ripple-container-content',
            position: 0,
            linear: [0, '#338AFF'],
            stripeEnable: false,
            // widthRatio: 0.4,
            lineCount: 50,
            mirrorEnable: true,
          });
        }, 300);
        rec.current.start(); // 开始录音
        // 计算时间，目前最大2min
        let time = 1 * 60;
        let downTimeInterval = setInterval(() => {
          time--;
          // 如果倒计时结束或者没有在录音了 就停止倒计时
          if (time < 0 || !onRecordRef.current) {
            checkAndFinshRec();
            clearInterval(downTimeInterval);
            clearInterval(intervalTime);
            setIntervalTime(null);
          } else if (time <= 10) {
            setDownTime(true);
          }
        }, 1000);
        setIntervalTime(downTimeInterval);
      },
      (msg: string, isUserNotAllow: boolean | string) => {
        if (isUserNotAllow) {
          isPermissionSucc.current = false;
          if (notAllowDialog) return;
          notAllowDialog = Dialog.alert({
            content: I18n.t(
              'the_recording_permission_is_not_turned_on__please_go_to_the_settings_page_to_tur',
              {},
              '录音权限未开启，请前往设置页面开启',
            ),
            onConfirm: async () => {
              recTmp.close();
              rec.current = null;
              setRecordStatus(false);
              notAllowDialog = null;
            },
          });
        }
      },
    );
  };
  // 松手发送录音
  const checkAndFinshRec = (e?: any) => {
    e && e.preventDefault();
    clearInterval(intervalTime);
    setIntervalTime(null);
    setDownTime(false);
    isTouched.current = false;
    let endTime = new Date().getTime();
    if (!rec.current || !isPermissionSucc.current) return;
    if (endTime - starRecTime < 1000 && rec) {
      Toast.show(
        I18n.t(
          'the_recording_time_is_too_short__please_re_record',
          {},
          '录音时间太短，请重新录制',
        ),
      );
      rec?.current.close();
      rec.current = null;
      setRecordStatus(false);
      return;
    }
    if (onRecordRef.current) {
      setRecordStatus(false);
      rec.current?.stop(
        async (blob: any, duration: number | string) => {
          rec.current.close(); // 释放录音资源，当然可以不释放，后面可以连续调用start；但不释放时系统或浏览器会一直提示在录音，最佳操作是录完就close掉
          rec.current = null;
          let file = new File([blob], `audio_${new Date().getTime()}`, {
            type: blob.type,
          });
          if (duration < 1000) {
            Toast.show(
              I18n.t(
                'the_recording_time_is_too_short__please_re_record',
                {},
                '录音时间太短，请重新录制',
              ),
            );
            return;
          }
          audioFile.current = file;
          setReocrdEnd(true);
          // 获取录音信息
          const audio = new Audio();
          audio.src = (window.URL || webkitURL).createObjectURL(blob);
          dealAudioTime(Number(duration) / 1000, true);
          audioDemo.current = audio;
          audio.ondurationchange = () => {
            dealAudioTime(audio.duration, true);
          };
          audio.onended = () => {
            setTimeout(() => {
              clearAudioDemo();
            }, 500);
          };
        },
        (msg: string) => {
          console.log('录音失败:' + msg);
          rec.current.close();
          rec.current = null;
        },
      );
    }
  };

  // 点击播放
  const callPlay = (isClose?: boolean) => {
    setIsPlay(isClose ? false : !isPlay);
    if (isPlay || isClose) {
      audioDemo.current.pause();
      clearInterval(processInterval.current);
      processInterval.current = null;
    } else {
      audioDemo.current.play();
      processInterval.current = window.setInterval(() => {
        setPlayTime();
      }, 1000);
    }
  };
  // 音量控制
  const handleVoice = () => {
    isVoiceOpen
      ? (audioDemo.current.muted = true)
      : (audioDemo.current.muted = false);
    setIsVoiceOpen(!isVoiceOpen);
  };
  // 点击 发送语音
  const sendVoice = () => {
    // 如果有语音试听需要先结束试听demo
    clearAudioDemo();
    let onLoading = Toast.show({
      content: I18n.t('voice_sending___', {}, '语音发送中...'),
      duration: 30000, // 超过30s自动关闭
    });
    // 上传音频
    let formData = new FormData();
    formData.append('file', audioFile.current);
    formData.append('userId', appContext.ext.imUserId);
    appContext.utils
      .fetch('/api/im-message/file/uploadMedia', {
        method: 'POST',
        // 上传文件需要删除Content-Type，由浏览器自己加
        transformRequest: [
          function (data, headers) {
            // 去除post请求默认的Content-Type
            delete headers.post['Content-Type'];
            return data;
          },
        ],
        data: formData,
      })
      .then(async (res: { vid: ''; fileName?: '' }) => {
        if (res?.vid) {
          await appContext.triggerEvent('send-audio-message', {
            vid: res?.vid,
            filename: res?.fileName,
            duration: realTotalTime.current,
          });
        } else {
          Toast.show(
            I18n.t(
              'voice_sending_failed__please_try_again_later',
              {},
              '语音发送失败，请稍后重试',
            ),
          );
        }
        onLoading.close();
      })
      .catch(() => {
        onLoading.close();
        Toast.show(
          I18n.t(
            'voice_sending_failed__please_try_again_later',
            {},
            '语音发送失败，请稍后重试',
          ),
        );
      });
    setReocrdEnd(false);
  };
  // 取消发送
  const cancelSend = () => {
    clearAudioDemo();
    setReocrdEnd(false);
  };
  // 倒计时弹窗
  const toastHandlerRef = useRef<ToastHandler>();
  useEffect(() => {
    if (downTime) {
      toastHandlerRef.current = Toast.show({
        content: <CountDownText />,
        duration: 10000,
      });
    } else {
      toastHandlerRef.current?.close();
    }
  }, [downTime]);

  useEffect(() => {
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    return () => {
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    };
  }, []);

  return (
    <>
      <div
        className={prefixCls(
          {
            [`${classes.prefix}AudioWrapper`]: true,
          },
          appContext?.query?.isPc,
        )}
      >
        <div
          className="recorder-btn"
          onMouseDown={checkAndStartRec}
          onMouseUp={checkAndFinshRec}
          onMouseLeave={checkAndFinshRec}
          onTouchStart={checkAndStartRec}
          onTouchEnd={checkAndFinshRec}
          onTouchCancel={checkAndFinshRec}
        >
          <div className="recorder-btn-trigger"></div>
          <div className={onRecord ? 'pull-up' : 'pull-down'}></div>
        </div>
        {onRecord && (
          <div
            className={`ripple-container ${
              !onRecord ? 'ripple-container-no' : ''
            }`}
          >
            <p className="ripple-container-tip">
              {I18n.t('please_speak', {}, '请说话')}
            </p>
            <div className="ripple-container-content"></div>
          </div>
        )}
        {isRecordEnd && (
          <div className="record-done-container">
            <p className="ripple-container-tip">
              {I18n.t('whether_to_send', {}, '是否发送')}
            </p>
            {/* 自定义播放器 */}
            <div className="record-play">
              {/* 播放按钮 */}
              <img
                onClick={() => callPlay()}
                className="audio-icon"
                src={isPlay ? pausepng : playpng}
              ></img>
              {/* 时间 */}
              <p className="time-container">
                <span>{playTime}</span>/<span>{totalTime}</span>
              </p>
              {/* 进度条 */}
              <div className="time-line">
                <div className="played-line" style={playedWidth}></div>
              </div>
              {/* 声音控制 */}
              {isVoiceOpen ? (
                <ColumnNotice
                  onClick={handleVoice}
                  theme="filled"
                  size="14"
                  fill="#666"
                />
              ) : (
                <ColumnMute
                  onClick={handleVoice}
                  theme="filled"
                  size="14"
                  fill="#666"
                />
              )}
            </div>
            <div className="btn-container">
              <Button
                onClick={cancelSend}
                className="cancel-btn"
                color="default"
              >
                {I18n.t('close_dialog_cancel_text', {}, '取消')}
              </Button>
              <Button onClick={sendVoice} className="send-btn" color="default">
                {I18n.t('send_voice', {}, '发送语音')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default inject<{
  appContext: AppContextType;
}>(({ appContext }) => ({
  appContext,
}))(observer(AudioWrapper));
